import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../lib/format';
import {
  DollarSign,
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Eye,
  Search,
  Download,
  User,
  Building2,
  ArrowLeft,
  Printer,
  X,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Loader2,
  Mail,
  ExternalLink,
  Package,
  Minus
} from 'lucide-react';
import { useUser } from '../store/useAuthStore';
import { apiClient } from '../lib/api';
import { Invoice, BulkOrder, User as IUser } from '@bloom/shared';
import { toast } from '../lib/toast';
import { InvoicePaymentModal } from '../components/payments/InvoicePaymentModal';
import { IssueCreditModal } from '../components/invoices/IssueCreditModal';
import { RefundInvoiceModal } from '../components/invoices/RefundInvoiceModal';
import { EditDraftModal } from '../components/invoices/EditDraftModal';
import { Plus, Pencil } from 'lucide-react';
import { formatDate as formatDateUtil, formatShortDate } from '../utils/dateUtils';
import { getCurrentTenantConfig } from '../config/tenant';

interface PaymentMethodInfo {
  payment_method_id: string;
  payment_method: string;
  payment_method_type?: string;
  card?: {
    last4: string;
    exp_month: string;
    exp_year: string;
    card_brand: string;
    card_holder_name?: string;
  };
  is_default: boolean;
}

interface PopulatedPaymentHistory {
  amount: number;
  paymentMethod: 'card' | 'ach' | 'check' | 'cash' | 'other';
  paymentDate: Date;
  transactionId?: string;
  notes?: string;
  recordedById?: IUser;
  status?: 'succeeded' | 'failed' | 'refunded'; // Payment attempt result
  errorMessage?: string; // Error message for failed payments
  errorCode?: string; // Error code from payment processor
}

interface PhysicianWithAM extends Omit<IUser, 'physicianData'> {
  physicianData?: {
    npiNumber?: string;
    distributorId?: {
      _id: string;
      profile?: { firstName?: string; lastName?: string };
      email?: string;
      distributorData?: { companyName?: string };
    };
    clinicAddress?: {
      street?: string;
      street2?: string;
      city?: string;
      state?: string;
      zipCode?: string;
    };
    arEnabled?: boolean;
    creditLimit?: number | null;
  };
}

interface PopulatedOrderRef {
  _id: string;
  orderNumber: string;
}

interface InvoiceWithDetails extends Omit<Invoice, 'paymentHistory' | 'physicianId' | 'createdById' | 'bulkOrderId' | 'orderIds'> {
  physicianId?: PhysicianWithAM;
  createdById?: IUser;
  bulkOrderId?: BulkOrder | string; // Populated bulk order object or unpopulated ObjectId string
  orderIds?: (PopulatedOrderRef | string)[]; // Populated when fetched via /invoices/:id
  paymentHistory: PopulatedPaymentHistory[];
  cancelled?: boolean; // Optional to handle legacy invoices
  volumeReward?: {
    tierLabel?: string;
    discountPercent?: number;
    discountAmount?: number;
    freeShippingApplied?: boolean;
    shippingWaivedAmount?: number;
    // 'discount' (clinic-pay) reduces the total; 'physician_markup' (patient-pay)
    // leaves the total whole and books the reward as physician earnings.
    appliedAs?: 'discount' | 'physician_markup';
    // Negotiated-deal subtotal excluded from the discount base (deal lines earn
    // tiers but are not discounted again).
    excludedDealSubtotal?: number;
  };
}

// Helper to get Account Manager name from physician's distributorId
const getAccountManagerName = (invoice: InvoiceWithDetails): string => {
  const am = invoice.physicianId?.physicianData?.distributorId;
  if (!am) return '-';
  
  // Try company name first, then full name
  if (am.distributorData?.companyName) {
    return am.distributorData.companyName;
  }
  if (am.profile?.firstName || am.profile?.lastName) {
    return `${am.profile?.firstName || ''} ${am.profile?.lastName || ''}`.trim();
  }
  return '-';
};

const getAccountManagerEmail = (invoice: InvoiceWithDetails): string => {
  const am = invoice.physicianId?.physicianData?.distributorId;
  return am?.email || '';
};

type InvoiceStatus = 'all' | 'draft' | 'sent' | 'paid' | 'overdue' | 'voided' | 'refunded' | 'partially_refunded';
type PaymentMethod = 'card' | 'ach' | 'check' | 'cash' | 'other';

// Renders deeplink(s) to the order detail modal on AdminOrders. Supports
// bulk invoices (single bulkOrderId) and standard invoices (orderIds[]).
function InvoiceOrderLinks({ invoice }: { invoice: InvoiceWithDetails }) {
  const bulk = invoice.bulkOrderId && typeof invoice.bulkOrderId === 'object'
    ? invoice.bulkOrderId
    : null;
  const populatedOrders = (invoice.orderIds || []).filter(
    (o): o is PopulatedOrderRef => typeof o === 'object' && !!o.orderNumber
  );

  if (!bulk && populatedOrders.length === 0) return null;

  const linkClass = "text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1";
  const label = bulk || populatedOrders.length === 1 ? 'Order:' : 'Orders:';

  return (
    <div className="flex justify-between items-start gap-2">
      <span className="text-gray-500">{label}</span>
      <div className="flex flex-col items-end gap-1">
        {bulk && (
          <a
            href={`/admin/orders?search=${bulk.bulkOrderNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            <Package className="h-3 w-3" />
            {bulk.bulkOrderNumber}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
        {!bulk && populatedOrders.map(o => (
          <a
            key={o._id}
            href={`/admin/orders?search=${o.orderNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            <Package className="h-3 w-3" />
            {o.orderNumber}
            <ExternalLink className="h-3 w-3" />
          </a>
        ))}
      </div>
    </div>
  );
}

// Print function for invoices
const printInvoice = (invoice: InvoiceWithDetails) => {

  const formatDate = (date: string | Date) => formatDateUtil(date, { month: 'long' });

  // Get tenant branding for logo
  const tenantConfig = getCurrentTenantConfig();
  const logoUrl = tenantConfig.branding.logo;
  // Resolve relative paths to absolute URLs for the print window
  const absoluteLogoUrl = logoUrl && logoUrl.startsWith('/') ? `${window.location.origin}${logoUrl}` : logoUrl;
  const companyName = tenantConfig.branding.companyName;

  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice ${invoice.invoiceNumber}</title>
      <style>
        @page {
          size: 8.5in 11in;
          margin: 1in;
        }
        @media print {
          body { margin: 0; }
          .no-print { display: none; }
        }
        body {
          font-family: Arial, sans-serif;
          line-height: 1.4;
          color: #000;
          padding: 20px;
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
          padding-bottom: 20px;
        }
        .header-logo {
          max-height: 60px;
          max-width: 200px;
          margin-bottom: 10px;
        }
        .invoice-title {
          font-size: 28px;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .invoice-number {
          font-size: 18px;
          color: #666;
        }
        .invoice-details {
          display: flex;
          justify-content: space-between;
          margin-bottom: 30px;
        }
        .billing-info, .invoice-info {
          width: 48%;
        }
        .section-title {
          font-size: 16px;
          font-weight: bold;
          margin-bottom: 10px;
          color: #333;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 30px;
        }
        .items-table th,
        .items-table td {
          border: 1px solid #ddd;
          padding: 8px 12px;
          text-align: left;
        }
        .items-table th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        .items-table .text-right {
          text-align: right;
        }
        .totals {
          width: 300px;
          margin-left: auto;
          margin-bottom: 30px;
        }
        .totals table {
          width: 100%;
          border-collapse: collapse;
        }
        .totals td {
          padding: 5px 10px;
          border-bottom: 1px solid #eee;
        }
        .totals .total-row {
          font-weight: bold;
          border-top: 2px solid #333;
          border-bottom: 2px solid #333;
        }
        .payment-status {
          background-color: #f0f9ff;
          border: 1px solid #0ea5e9;
          border-radius: 4px;
          padding: 15px;
          margin-top: 20px;
        }
        .paid-status {
          background-color: #f0fdf4;
          border-color: #22c55e;
        }
        .overdue-status {
          background-color: #fef2f2;
          border-color: #ef4444;
        }
      </style>
    </head>
    <body>
      <div class="header">
        ${absoluteLogoUrl ? `<img src="${absoluteLogoUrl}" alt="${companyName}" class="header-logo" />` : ''}
        <div class="invoice-title">INVOICE</div>
        <div class="invoice-number">${invoice.invoiceNumber}</div>
      </div>

      <div class="invoice-details">
        <div class="billing-info">
          <div class="section-title">Bill To:</div>
          <div>Dr. ${invoice.physicianId?.profile?.firstName} ${invoice.physicianId?.profile?.lastName}</div>
          ${invoice.physicianId?.profile?.clinicName ? `<div>${invoice.physicianId.profile.clinicName}</div>` : ''}
          ${invoice.physicianId?.physicianData?.clinicAddress ? `
            <div>${invoice.physicianId.physicianData.clinicAddress.street || ''}</div>
            ${invoice.physicianId.physicianData.clinicAddress.street2 ? `<div>${invoice.physicianId.physicianData.clinicAddress.street2}</div>` : ''}
            <div>${invoice.physicianId.physicianData.clinicAddress.city || ''}, ${invoice.physicianId.physicianData.clinicAddress.state || ''} ${invoice.physicianId.physicianData.clinicAddress.zipCode || ''}</div>
          ` : ''}
          <div>${invoice.physicianId?.email}</div>
          ${invoice.physicianId?.physicianData?.npiNumber ? `<div>NPI: ${invoice.physicianId.physicianData.npiNumber}</div>` : ''}
        </div>
        <div class="invoice-info">
          <div class="section-title">Invoice Details:</div>
          <div><strong>Date:</strong> ${formatDate(invoice.createdAt)}</div>
          <div><strong>Due Date:</strong> ${formatDate(invoice.dueDate)}</div>
          <div><strong>Payment Terms:</strong> ${invoice.physicianId?.physicianData?.arEnabled ? invoice.paymentTerms : 'Due upon receipt'}</div>
          <div><strong>Status:</strong> ${invoice.status.toUpperCase()}</div>
        </div>
      </div>

      <table class="items-table">
        <thead>
          <tr>
            <th>Description</th>
            <th class="text-right">Quantity</th>
            <th class="text-right">Unit Price</th>
            <th class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          ${invoice.items.map(item => `
            <tr>
              <td>${item.description}</td>
              <td class="text-right">${item.quantity}</td>
              <td class="text-right">${formatCurrency(item.unitPrice)}</td>
              <td class="text-right">${formatCurrency(item.totalPrice)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <div class="totals">
        <table>
          <tr>
            <td>Subtotal:</td>
            <td class="text-right">${formatCurrency(invoice.subtotal)}</td>
          </tr>
          ${invoice.shippingAmount > 0 ? `
          <tr>
            <td>Shipping:</td>
            <td class="text-right">${formatCurrency(invoice.shippingAmount)}</td>
          </tr>
          ` : ''}
          ${(invoice.telemedNetworkFee || 0) > 0 ? `
          <tr>
            <td>Telemed network fee:</td>
            <td class="text-right">${formatCurrency(invoice.telemedNetworkFee || 0)}</td>
          </tr>
          ` : ''}
          <tr>
            <td>Tax:</td>
            <td class="text-right">${formatCurrency(invoice.taxAmount)}</td>
          </tr>
          ${invoice.volumeReward?.discountAmount ? `
          <tr>
            <td>Volume Reward (${invoice.volumeReward.tierLabel} ${invoice.volumeReward.discountPercent}%)${invoice.volumeReward.appliedAs === 'physician_markup' ? ' — physician markup credit' : ''}:</td>
            <td class="text-right">${invoice.volumeReward.appliedAs === 'physician_markup' ? '+' : '−'}${formatCurrency(invoice.volumeReward.discountAmount)}</td>
          </tr>
          ` : ''}
          ${invoice.volumeReward?.excludedDealSubtotal ? `
          <tr>
            <td colspan="2" style="font-size: 11px; color: #666; font-style: italic; padding-top: 0;">
              ${formatCurrency(invoice.volumeReward.excludedDealSubtotal)} of negotiated-deal pricing is excluded from the volume reward.
            </td>
          </tr>
          ` : ''}
          <tr class="total-row">
            <td>Total:</td>
            <td class="text-right">${formatCurrency(invoice.totalAmount)}</td>
          </tr>
          <tr>
            <td>Amount Paid:</td>
            <td class="text-right">${formatCurrency(invoice.amountPaid)}</td>
          </tr>
          <tr>
            <td><strong>Amount Due:</strong></td>
            <td class="text-right"><strong>${formatCurrency(invoice.amountDue)}</strong></td>
          </tr>
        </table>
      </div>

      ${invoice.status === 'paid' && invoice.paidAt ? `
        <div class="payment-status paid-status">
          <strong>✓ PAID IN FULL</strong><br>
          Payment completed on ${formatDate(invoice.paidAt)}
        </div>
      ` : invoice.status === 'overdue' ? `
        <div class="payment-status overdue-status">
          <strong>⚠ OVERDUE</strong><br>
          Payment was due on ${formatDate(invoice.dueDate)}
        </div>
      ` : ''}
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  }
};

interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

export const AdminInvoices: React.FC = () => {
  const user = useUser();
  const canRecordPayment = user?.adminData?.canRecordPayment === true;
  const canIssueRefunds = user?.adminData?.canIssueRefunds === true;
  const [showRefundModal, setShowRefundModal] = useState(false);

  // State for invoices
  const [invoices, setInvoices] = useState<InvoiceWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Stats state (for header cards)
  const [stats, setStats] = useState({
    total: 0,
    paid: 0,
    sent: 0,
    overdue: 0,
    draft: 0,
    cancelled: 0
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState<InvoiceStatus>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Modal states
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceWithDetails | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  

  
  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMethod: 'card' as PaymentMethod,
    transactionId: '',
    notes: ''
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [cancellingInvoice, setCancellingInvoice] = useState<string | null>(null);
  // Invoice pending void confirmation (null = modal closed)
  const [invoiceToVoid, setInvoiceToVoid] = useState<InvoiceWithDetails | null>(null);

  // Charge card modal state
  const [showChargeCardModal, setShowChargeCardModal] = useState(false);
  const [physicianPaymentMethods, setPhysicianPaymentMethods] = useState<PaymentMethodInfo[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>('');
  const [chargingCard, setChargingCard] = useState(false);

  // Add new card modal state (using Hyperswitch checkout)
  const [showAddCardModal, setShowAddCardModal] = useState(false);

  // Credit memo / edit draft state
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showEditDraftModal, setShowEditDraftModal] = useState(false);

  // Send payment link state
  const [sendingPaymentLink, setSendingPaymentLink] = useState<string | null>(null);

  // Check for payment success/failure from Hyperswitch redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectStatus = params.get('redirect_status');
    const paymentStatus = params.get('payment');
    const invoiceNumber = params.get('invoice');

    // Clean up URL params (but not invoiceId - that's handled separately)
    if (redirectStatus || paymentStatus || invoiceNumber) {
      const url = new URL(window.location.href);
      url.searchParams.delete('redirect_status');
      url.searchParams.delete('payment');
      url.searchParams.delete('invoice');
      window.history.replaceState({}, '', url.toString());
    }

    // Show appropriate toast based on payment result
    if (redirectStatus === 'succeeded' || paymentStatus === 'success') {
      toast.success(
        invoiceNumber
          ? `Payment successful for invoice ${invoiceNumber}`
          : 'Payment processed successfully'
      );
    } else if (redirectStatus === 'failed') {
      toast.error('Payment failed. Please try again.');
    } else if (redirectStatus === 'requires_payment_method') {
      toast.error('Payment could not be processed. Please try a different payment method.');
    }
  }, []);

  // Handle invoiceId query param to auto-open invoice details or charge modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invoiceId = params.get('invoiceId');
    const action = params.get('action');

    if (invoiceId && !loading) {
      // Clean up URL params
      window.history.replaceState({}, '', window.location.pathname);

      // Fetch invoice details and open appropriate modal
      const fetchAndSelectInvoice = async () => {
        try {
          const response = await apiClient.get<{ success: boolean; data: InvoiceWithDetails }>(`/invoices/${invoiceId}`);
          if (response.success && response.data) {
            setSelectedInvoice(response.data);

            // Open charge modal if action=charge, otherwise open details modal
            if (action === 'charge' && response.data.status !== 'paid' && response.data.status !== 'voided') {
              openChargeCardModal(response.data);
            } else {
              setShowDetailsModal(true);
            }
          } else {
            toast.error('Invoice not found');
          }
        } catch (error) {
          console.error('Error fetching invoice:', error);
          toast.error('Failed to load invoice details');
        }
      };

      fetchAndSelectInvoice();
    }
  }, [loading]);

  // ESC key handler to close modals
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (showDetailsModal) {
          setShowDetailsModal(false);
        }
        if (showChargeCardModal) {
          setShowChargeCardModal(false);
        }
        if (showPaymentModal) {
          setShowPaymentModal(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [showDetailsModal, showChargeCardModal, showPaymentModal]);

  // Open invoice details modal
  const openInvoiceDetails = async (invoice: InvoiceWithDetails) => {
    setLoading(true);
    try {
      const responseData = await apiClient.get(`/invoices/${invoice._id}`);
      if (responseData.success) {
        setSelectedInvoice(responseData.data);
        setShowDetailsModal(true);
      } else {
        setError(`Failed to load invoice details: ${responseData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error loading invoice details:', err);
      setError(`Failed to load invoice details: ${err instanceof Error ? err.message : 'Network error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Format currency

  // Format date (uses Eastern timezone)
  const formatDate = (date: string | Date) => formatDateUtil(date);

  // Export to CSV with detailed information
  const exportToCSV = () => {
    const headers = [
      // Basic Invoice Information
      'Invoice Number',
      'Status',
      'Created Date',
      'Due Date',
      'Payment Terms',
      'Paid At',
      
      // Physician Information
      'Physician Name',
      'Physician Email',
      'Physician Phone',
      'Physician NPI',
      
      // Account Manager Information
      'Account Manager Name',
      'Account Manager Email',
      'Created By Role',
      
      // Financial Summary
      'Subtotal',
      'Shipping',
      'Telemed Network Fee',
      'Tax Amount',
      'Total Amount',
      'Amount Paid',
      'Amount Due',
      
      // Invoice Items (concatenated)
      'Items Count',
      'Items Descriptions',
      'Items Quantities',
      'Items Unit Prices',
      'Items Totals',
      
      // Payment History Summary
      'Payment Count',
      'Payment Dates',
      'Payment Amounts',
      'Payment Methods',
      'Transaction IDs',
      'Payment Notes',
      
      // Additional Details
      'Bulk Order ID',
      'Cancelled',
      'Refunded Amount',
      'Full Items Detail'
    ];

    const csvData = filteredInvoices.map(invoice => {
      // Format payment history
      const payments = invoice.paymentHistory || [];
      const paymentDates = payments.map(p => formatDate(p.paymentDate)).join(' | ');
      const paymentAmounts = payments.map(p => formatCurrency(p.amount)).join(' | ');
      const paymentMethods = payments.map(p => p.paymentMethod).join(' | ');
      const transactionIds = payments.map(p => p.transactionId || '').filter(Boolean).join(' | ');
      const paymentNotes = payments.map(p => p.notes || '').filter(Boolean).join(' | ');
      
      // Format invoice items
      const items = invoice.items || [];
      const itemDescriptions = items.map(item => item.description).join(' | ');
      const itemQuantities = items.map(item => item.quantity.toString()).join(' | ');
      const itemUnitPrices = items.map(item => formatCurrency(item.unitPrice)).join(' | ');
      const itemTotals = items.map(item => formatCurrency(item.totalPrice)).join(' | ');
      
      // Full items detail for reference
      const fullItemsDetail = items.map(item => 
        `${item.description} (Qty: ${item.quantity}, Unit: ${formatCurrency(item.unitPrice)}, Total: ${formatCurrency(item.totalPrice)})`
      ).join(' | ');

      return [
        // Basic Invoice Information
        invoice.invoiceNumber,
        invoice.status,
        formatDate(invoice.createdAt),
        formatDate(invoice.dueDate),
        invoice.physicianId?.physicianData?.arEnabled ? invoice.paymentTerms : 'Due upon receipt',
        invoice.paidAt ? formatDate(invoice.paidAt) : '',
        
        // Physician Information
        `Dr. ${invoice.physicianId?.profile?.firstName || ''} ${invoice.physicianId?.profile?.lastName || ''}`.trim(),
        invoice.physicianId?.email || '',
        invoice.physicianId?.profile?.phone || '',
        invoice.physicianId?.physicianData?.npiNumber || '',
        
        // Account Manager Information
        getAccountManagerName(invoice),
        getAccountManagerEmail(invoice),
        invoice.createdByRole || '',
        
        // Financial Summary
        formatCurrency(invoice.subtotal),
        formatCurrency(invoice.shippingAmount || 0),
        formatCurrency(invoice.telemedNetworkFee || 0),
        formatCurrency(invoice.taxAmount),
        formatCurrency(invoice.totalAmount),
        formatCurrency(invoice.amountPaid),
        formatCurrency(invoice.amountDue),
        
        // Invoice Items
        items.length.toString(),
        itemDescriptions,
        itemQuantities,
        itemUnitPrices,
        itemTotals,
        
        // Payment History Summary
        payments.length.toString(),
        paymentDates,
        paymentAmounts,
        paymentMethods,
        transactionIds,
        paymentNotes,
        
        // Additional Details
        (typeof invoice.bulkOrderId === 'object' ? invoice.bulkOrderId?._id : invoice.bulkOrderId) || '',
        // A partially refunded invoice is NOT cancelled — most of it stands, and
        // exporting it as cancelled would misstate the period to accounting. The
        // refunded figure is its own column instead.
        (invoice.status === 'voided' || invoice.status === 'refunded') ? 'Yes' : 'No',
        (invoice.refundAmount || 0).toFixed(2),
        fullItemsDetail
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `invoices-detailed-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load invoices with pagination. Only the latest request may fill the
  // table: a slow answer to an earlier search, filter or page is dropped.
  const loadSeq = useRef(0);
  const loadInvoices = async (page: number = 1) => {
    const mine = ++loadSeq.current;
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(debouncedSearch && { search: debouncedSearch })
      });
      
      const response = await apiClient.get<{
        success: boolean;
        data: InvoiceWithDetails[];
        pagination: PaginationInfo;
        stats?: {
          total: number;
          paid: number;
          sent: number;
          overdue: number;
          draft: number;
          cancelled: number;
        };
        error?: string;
      }>(`/invoices?${params.toString()}`);
      if (mine !== loadSeq.current) return;

      if (response.success && response.data) {
        setInvoices(response.data);
        if (response.pagination) {
          setPagination(response.pagination);
        }
        if (response.stats) {
          setStats(response.stats);
        }
      } else {
        setError(response.error || 'Failed to load invoices');
      }
    } catch (error: any) {
      if (mine !== loadSeq.current) return;
      console.error('Error loading invoices:', error);
      setError('Failed to load invoices');
    } finally {
      if (mine === loadSeq.current) setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    loadInvoices(newPage);
  };

  // Mark invoice as paid
  const markAsPaid = async (invoiceId: string, fullPayment: boolean = false) => {
    try {
      setSubmittingPayment(true);
      
      if (fullPayment) {
        // Mark as fully paid
        const response = await apiClient.post(`/invoices/${invoiceId}/mark-paid`, {
          paymentMethod: paymentForm.paymentMethod,
          transactionId: paymentForm.transactionId || undefined,
          notes: paymentForm.notes || undefined
        });
        
        if (response.success) {
          await loadInvoices(); // Reload to get updated data
          setShowPaymentModal(false);
          resetPaymentForm();
        } else {
          setError(response.error || 'Failed to mark invoice as paid');
        }
      } else {
        // Add partial payment
        const amount = parseFloat(paymentForm.amount);
        if (isNaN(amount) || amount <= 0) {
          setError('Please enter a valid payment amount');
          return;
        }
        
        const response = await apiClient.post(`/invoices/${invoiceId}/add-payment`, {
          amount,
          paymentMethod: paymentForm.paymentMethod,
          transactionId: paymentForm.transactionId || undefined,
          notes: paymentForm.notes || undefined
        });
        
        if (response.success) {
          await loadInvoices(); // Reload to get updated data
          setShowPaymentModal(false);
          resetPaymentForm();
        } else {
          setError(response.error || 'Failed to add payment');
        }
      }
    } catch (error: any) {
      console.error('Error processing payment:', error);
      setError('Failed to process payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  // Reset payment form
  const resetPaymentForm = () => {
    setPaymentForm({
      amount: '',
      paymentMethod: 'card',
      transactionId: '',
      notes: ''
    });
  };

  // Load payment methods for a physician
  const loadPhysicianPaymentMethods = async (physicianId: string) => {
    try {
      setLoadingPaymentMethods(true);
      const response = await apiClient.getPhysicianPaymentMethods(physicianId);
      
      if (response.success && response.data) {
        setPhysicianPaymentMethods(response.data.paymentMethods || []);
        // Pre-select default or first payment method
        const defaultPm = response.data.paymentMethods?.find((pm: PaymentMethodInfo) => pm.is_default);
        if (defaultPm) {
          setSelectedPaymentMethodId(defaultPm.payment_method_id);
        } else if (response.data.paymentMethods?.length > 0) {
          setSelectedPaymentMethodId(response.data.paymentMethods[0].payment_method_id);
        }
      } else {
        setPhysicianPaymentMethods([]);
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
      setPhysicianPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Open charge card modal
  const openChargeCardModal = async (invoice: InvoiceWithDetails) => {
    setSelectedInvoice(invoice);
    setShowChargeCardModal(true);
    
    // Load physician's payment methods
    const physicianId = typeof invoice.physicianId === 'object' 
      ? invoice.physicianId?._id 
      : invoice.physicianId;
    
    if (physicianId) {
      await loadPhysicianPaymentMethods(physicianId.toString());
    }
  };

  // Charge the selected card
  const chargeCard = async () => {
    if (!selectedInvoice || !selectedPaymentMethodId) {
      toast.error('Please select a payment method');
      return;
    }

    try {
      setChargingCard(true);
      const response = await apiClient.chargeInvoice(selectedInvoice._id, selectedPaymentMethodId);
      
      if (response.success) {
        toast.success('Payment initiated successfully');
        setShowChargeCardModal(false);
        setSelectedPaymentMethodId('');
        setPhysicianPaymentMethods([]);
        await loadInvoices(); // Refresh invoices
      } else {
        toast.error(response.error || 'Failed to charge card');
      }
    } catch (error: any) {
      console.error('Error charging card:', error);
      toast.error(error.message || 'Failed to charge card');
    } finally {
      setChargingCard(false);
    }
  };

  // Cancel invoice
  const cancelInvoice = async (invoiceId: string) => {
    try {
      setInvoiceToVoid(null); // close the confirm modal; row button shows the spinner
      setCancellingInvoice(invoiceId);

      const response = await apiClient.put(`/invoices/${invoiceId}/cancel`);

      if (response.success) {
        await loadInvoices(); // Reload to get updated data
        toast.success('Invoice cancelled successfully');
      } else {
        toast.error(response.error || 'Failed to cancel invoice');
      }
    } catch (error: any) {
      console.error('Error cancelling invoice:', error);
      toast.error('Failed to cancel invoice');
    } finally {
      setCancellingInvoice(null);
    }
  };

  // Send payment link to physician
  const sendPaymentLink = async (invoiceId: string) => {
    try {
      setSendingPaymentLink(invoiceId);

      const response = await apiClient.post(`/invoices/${invoiceId}/send-payment-link`);

      if (response.success) {
        await loadInvoices(); // Reload to get updated data with payment link info
        toast.success('Payment link sent successfully');
      } else {
        toast.error(response.error || 'Failed to send payment link');
      }
    } catch (error: any) {
      console.error('Error sending payment link:', error);
      toast.error(error.message || 'Failed to send payment link');
    } finally {
      setSendingPaymentLink(null);
    }
  };

  // Invoices are filtered server-side, but we can add instant client-side filtering 
  // while the debounced search is pending for better UX
  const filteredInvoices = React.useMemo(() => {
    // If the search term differs from debounced, apply client-side filter for instant feedback
    if (searchTerm && searchTerm !== debouncedSearch) {
      const term = searchTerm.toLowerCase();
      return invoices.filter(invoice =>
        invoice.invoiceNumber.toLowerCase().includes(term) ||
        invoice.physicianId?.profile?.firstName?.toLowerCase().includes(term) ||
        invoice.physicianId?.profile?.lastName?.toLowerCase().includes(term) ||
        invoice.physicianId?.profile?.clinicName?.toLowerCase().includes(term) ||
        invoice.physicianId?.email?.toLowerCase().includes(term)
      );
    }
    // Otherwise, the server has already filtered
    return invoices;
  }, [invoices, searchTerm, debouncedSearch]);

  // Load data on mount and when filters change
  useEffect(() => {
    if (user?.role === 'admin') {
      loadInvoices(1); // Reset to page 1 when filter changes
    }
  }, [user, statusFilter, debouncedSearch]);

  // Get status badge color
  const getStatusBadge = (invoice: InvoiceWithDetails) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
      sent: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Sent' },
      paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Paid' },
      overdue: { bg: 'bg-red-100', text: 'text-red-800', label: 'Overdue' },
      voided: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Voided' },
      refunded: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Refunded' },
      // Amber rather than orange, and it carries the amount: a $10-of-$500 refund
      // reading identically to a full one is how a human makes the next mistake.
      partially_refunded: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Part. refunded' }
    };

    const badge = badges[invoice.status] || badges.draft;
    if (invoice.status === 'partially_refunded' && invoice.refundAmount) {
      badge.label = `Part. refunded $${invoice.refundAmount.toFixed(2)}`;
    }

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // Check if user is admin
  if (user?.role !== 'admin') {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Access Denied</h3>
              <div className="mt-2 text-sm text-red-700">
                You need admin privileges to access invoice management.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Invoice Management</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage all invoices and payments</p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button
            onClick={exportToCSV}
            disabled={loading || filteredInvoices.length === 0}
            className="inline-flex items-center justify-center px-4 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 w-full sm:w-auto"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Detailed CSV ({filteredInvoices.length})
          </button>
          <button
            onClick={() => loadInvoices()}
            disabled={loading}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 w-full sm:w-auto"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards - Clickable to filter */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <button
          onClick={() => setStatusFilter('all')}
          className={`bg-white rounded-lg shadow p-4 sm:p-6 text-left transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Total Invoices</p>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('paid')}
          className={`bg-white rounded-lg shadow p-4 sm:p-6 text-left transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === 'paid' ? 'ring-2 ring-green-500' : ''}`}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle2 className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Paid</p>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.paid}</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('sent')}
          className={`bg-white rounded-lg shadow p-4 sm:p-6 text-left transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === 'sent' ? 'ring-2 ring-yellow-500' : ''}`}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Pending</p>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.sent}</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setStatusFilter('overdue')}
          className={`bg-white rounded-lg shadow p-4 sm:p-6 text-left transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === 'overdue' ? 'ring-2 ring-red-500' : ''}`}
        >
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
            </div>
            <div className="ml-3 sm:ml-4 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-500 truncate">Overdue</p>
              <p className="text-lg sm:text-2xl font-semibold text-gray-900">{stats.overdue}</p>
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Invoices
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Invoice number, physician name..."
                className="pl-10 w-full border border-gray-300 rounded-md py-3 sm:py-2 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status Filter
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as InvoiceStatus)}
              className="w-full border border-gray-300 rounded-md py-3 sm:py-2 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base sm:text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="voided">Voided</option>
              <option value="refunded">Refunded</option>
              <option value="partially_refunded">Partially refunded</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
              }}
              className="w-full bg-gray-100 text-gray-700 py-3 sm:py-2 px-4 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-600 hover:text-red-500"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Invoices List */}
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow p-4 sm:px-6 sm:py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            Invoices ({filteredInvoices.length})
          </h2>
        </div>
        
        {loading ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading invoices...</p>
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No invoices found matching your criteria.</p>
          </div>
        ) : (
          <>
            {/* Desktop/Tablet: Table view - Responsive widths */}
            <div className="hidden md:block data-table-container">
              <div className="data-table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Physician</th>
                      <th>Account Manager</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Due Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.map((invoice) => (
                      <tr
                        key={invoice._id}
                        onClick={() => openInvoiceDetails(invoice)}
                        className="cursor-pointer hover:bg-gray-50"
                      >
                        <td className="cell-primary">
                          <div className="cell-inline">
                            <FileText className="h-4 w-4 cell-inline-icon flex-shrink-0" />
                            <div className="cell-stack">
                              <span className="cell-stack-value cell-truncate">{invoice.invoiceNumber}</span>
                              <span className="cell-stack-label">{formatDate(invoice.createdAt)}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="cell-stack">
                            <span className="cell-stack-value cell-truncate">
                              Dr. {invoice.physicianId?.profile?.firstName} {invoice.physicianId?.profile?.lastName}
                            </span>
                            {invoice.physicianId?.profile?.clinicName && (
                              <span className="cell-stack-label cell-truncate">{invoice.physicianId.profile.clinicName}</span>
                            )}
                            <span className="cell-stack-label cell-truncate" title={invoice.physicianId?.email}>
                              {invoice.physicianId?.email && invoice.physicianId.email.length > 30
                                ? invoice.physicianId.email.substring(0, 30) + '...'
                                : invoice.physicianId?.email}
                            </span>
                          </div>
                        </td>
                        <td>
                          {invoice.physicianId?.physicianData?.distributorId ? (
                            <div className="cell-stack">
                              <span className="cell-stack-value cell-truncate">{getAccountManagerName(invoice)}</span>
                              <span className="cell-stack-label cell-truncate">{getAccountManagerEmail(invoice)}</span>
                            </div>
                          ) : (
                            <span className="cell-muted">-</span>
                          )}
                        </td>
                        <td className="cell-mono">
                          <div className="cell-stack">
                            <span className="cell-stack-value">{formatCurrency(invoice.totalAmount)}</span>
                            <span className="cell-stack-label">Paid: {formatCurrency(invoice.amountPaid)}</span>
                            {invoice.amountDue > 0 && (
                              <span className="text-xs text-red-600 font-medium">Due: {formatCurrency(invoice.amountDue)}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {getStatusBadge(invoice)}
                        </td>
                        <td className="cell-secondary">
                          {formatDate(invoice.dueDate)}
                        </td>
                        <td className="cell-actions" onClick={(e) => e.stopPropagation()}>
                          <div className="table-actions flex-wrap lg:flex-nowrap">
                            <button
                              onClick={() => openInvoiceDetails(invoice)}
                              className="table-action-btn table-action-btn-secondary"
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              <span className="hidden lg:inline">View</span>
                            </button>
                            {invoice.status !== 'paid' && invoice.status !== 'voided' && invoice.status !== 'refunded' && (
                              <>
                                <button
                                  onClick={() => openChargeCardModal(invoice)}
                                  className="table-action-btn table-action-btn-primary"
                                  title="Charge saved card"
                                >
                                  <CreditCard className="h-3 w-3 mr-1" />
                                  <span className="hidden lg:inline">Charge</span>
                                </button>
                                {canRecordPayment && (
                                <button
                                  onClick={() => {
                                    setSelectedInvoice(invoice);
                                    setShowPaymentModal(true);
                                  }}
                                  className="table-action-btn table-action-btn-success"
                                >
                                  <DollarSign className="h-3 w-3 mr-1" />
                                  <span className="hidden lg:inline">Record</span>
                                </button>
                                )}
                                <button
                                  onClick={() => sendPaymentLink(invoice._id)}
                                  disabled={sendingPaymentLink === invoice._id}
                                  className="table-action-btn table-action-btn-purple"
                                  title={invoice.paymentLinkSentAt ? `Last sent ${formatShortDate(invoice.paymentLinkSentAt)}` : 'Send payment link via email'}
                                >
                                  {sendingPaymentLink === invoice._id ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  ) : (
                                    <Mail className="h-3 w-3 mr-1" />
                                  )}
                                  <span className="hidden lg:inline">{invoice.paymentLinkSentAt ? 'Resend' : 'Send'}</span>
                                </button>
                              </>
                            )}
                            {invoice.status !== 'voided' && invoice.status !== 'refunded' && invoice.status !== 'paid' && invoice.amountPaid === 0 && (
                              <button
                                onClick={() => setInvoiceToVoid(invoice)}
                                disabled={cancellingInvoice === invoice._id}
                                className="table-action-btn table-action-btn-danger"
                              >
                                <XCircle className="h-3 w-3 mr-1" />
                                <span className="hidden lg:inline">{cancellingInvoice === invoice._id ? '...' : 'Void'}</span>
                              </button>
                            )}
                            {invoice.status === 'draft' && (
                              <button
                                onClick={() => { setSelectedInvoice(invoice); setShowEditDraftModal(true); }}
                                className="table-action-btn table-action-btn-secondary"
                                title="Edit draft invoice"
                              >
                                <Pencil className="h-3 w-3 mr-1" />
                                <span className="hidden lg:inline">Edit</span>
                              </button>
                            )}
                            {(invoice.status === 'sent' || invoice.status === 'overdue' || invoice.status === 'paid') && (
                              <button
                                onClick={() => { setSelectedInvoice(invoice); setShowCreditModal(true); }}
                                className="table-action-btn table-action-btn-warning"
                                title="Issue credit memo"
                              >
                                <Minus className="h-3 w-3 mr-1" />
                                <span className="hidden lg:inline">Credit</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile: Card view */}
            <div className="md:hidden space-y-3">
              {filteredInvoices.map((invoice) => (
                <div key={invoice._id} className="bg-white rounded-lg shadow p-4 sm:p-6">
                  <div className="flex flex-col space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center min-w-0 flex-1">
                        <FileText className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <h3 className="text-base font-medium text-gray-900 truncate">
                            {invoice.invoiceNumber}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Created {formatDate(invoice.createdAt)}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(invoice)}
                    </div>
                    
                    <div className="flex items-center">
                      <User className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900">
                          Dr. {invoice.physicianId?.profile?.firstName} {invoice.physicianId?.profile?.lastName}
                        </div>
                        {invoice.physicianId?.profile?.clinicName && (
                          <div className="text-sm text-gray-600 truncate">
                            {invoice.physicianId.profile.clinicName}
                          </div>
                        )}
                        <div className="text-sm text-gray-500 truncate" title={invoice.physicianId?.email}>
                          {invoice.physicianId?.email && invoice.physicianId.email.length > 30
                            ? invoice.physicianId.email.substring(0, 30) + '...'
                            : invoice.physicianId?.email}
                        </div>
                      </div>
                    </div>

                    {invoice.physicianId?.physicianData?.distributorId && (
                      <div className="flex items-center">
                        <Building2 className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900">
                            {getAccountManagerName(invoice)}
                          </div>
                          <div className="text-sm text-gray-500 truncate">
                            {getAccountManagerEmail(invoice)}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Total Amount:</span>
                        <div className="font-medium text-gray-900">
                          {formatCurrency(invoice.totalAmount)}
                        </div>
                        <div className="text-gray-500">
                          Paid: {formatCurrency(invoice.amountPaid)}
                        </div>
                        {invoice.amountDue > 0 && (
                          <div className="text-red-600">
                            Due: {formatCurrency(invoice.amountDue)}
                          </div>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-500">Due Date:</span>
                        <div className="font-medium text-gray-900">
                          {formatDate(invoice.dueDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={async () => {
                        setLoading(true);
                        try {
                          const responseData = await apiClient.get(`/invoices/${invoice._id}`);
                          
                          if (responseData.success) {
                            setSelectedInvoice(responseData.data);
                            setShowDetailsModal(true);
                          } else {
                            setError(`Failed to load invoice details: ${responseData.error || 'Unknown error'}`);
                          }
                        } catch (err) {
                          console.error('Error loading invoice details:', err);
                          setError(`Failed to load invoice details: ${err instanceof Error ? err.message : 'Network error'}`);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </button>
                    {invoice.status !== 'paid' && invoice.status !== 'voided' && invoice.status !== 'refunded' && (
                      <>
                        <button
                          onClick={() => openChargeCardModal(invoice)}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <CreditCard className="h-4 w-4 mr-2" />
                          Charge Card
                        </button>
                        {canRecordPayment && (
                        <button
                          onClick={() => {
                            setSelectedInvoice(invoice);
                            setShowPaymentModal(true);
                          }}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <DollarSign className="h-4 w-4 mr-2" />
                          Record
                        </button>
                        )}
                        <button
                          onClick={() => sendPaymentLink(invoice._id)}
                          disabled={sendingPaymentLink === invoice._id}
                          className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                        >
                          {sendingPaymentLink === invoice._id ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4 mr-2" />
                          )}
                          {invoice.paymentLinkSentAt ? 'Resend Link' : 'Send Link'}
                        </button>
                      </>
                    )}
                    {invoice.status !== 'voided' && invoice.status !== 'refunded' && invoice.status !== 'paid' && invoice.amountPaid === 0 && (
                      <button
                        onClick={() => setInvoiceToVoid(invoice)}
                        disabled={cancellingInvoice === invoice._id}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        {cancellingInvoice === invoice._id ? '...' : 'Void'}
                      </button>
                    )}
                    {invoice.status === 'draft' && (
                      <button
                        onClick={() => { setSelectedInvoice(invoice); setShowEditDraftModal(true); }}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit Draft
                      </button>
                    )}
                    {(invoice.status === 'sent' || invoice.status === 'overdue' || invoice.status === 'paid') && (
                      <button
                        onClick={() => { setSelectedInvoice(invoice); setShowCreditModal(true); }}
                        className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-amber-600 hover:bg-amber-700"
                      >
                        <Minus className="h-4 w-4 mr-2" />
                        Issue Credit
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && (
              <div className="bg-white rounded-lg shadow px-4 py-3 flex items-center justify-between sm:px-6 mt-4">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPrevPage || loading}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-700 self-center">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage || loading}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span>
                      {' '}-{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.totalCount)}
                      </span>
                      {' '}of{' '}
                      <span className="font-medium">{pagination.totalCount}</span>
                      {' '}invoices
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.hasPrevPage || loading}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.page >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = pagination.page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pageNum === pagination.page
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            } disabled:opacity-50`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.hasNextPage || loading}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Record Payment - {selectedInvoice.invoiceNumber}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder={`Max: ${formatCurrency(selectedInvoice.amountDue)}`}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Amount due: {formatCurrency(selectedInvoice.amountDue)}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Method
                  </label>
                  <select
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, paymentMethod: e.target.value as PaymentMethod }))}
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="card">Credit Card</option>
                    <option value="ach">ACH Transfer</option>
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction ID (Optional)
                  </label>
                  <input
                    type="text"
                    value={paymentForm.transactionId}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, transactionId: e.target.value }))}
                    placeholder="Transaction or reference ID"
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    placeholder="Payment notes..."
                    className="w-full border border-gray-300 rounded-md py-2 px-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                <div className="flex space-x-2 pt-4">
                  <button
                    onClick={() => markAsPaid(selectedInvoice._id, false)}
                    disabled={submittingPayment || !paymentForm.amount}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submittingPayment ? 'Processing...' : 'Add Payment'}
                  </button>
                  <button
                    onClick={() => markAsPaid(selectedInvoice._id, true)}
                    disabled={submittingPayment}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {submittingPayment ? 'Processing...' : 'Mark Paid'}
                  </button>
                </div>
                
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    resetPaymentForm();
                  }}
                  className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charge Card Modal */}
      {showChargeCardModal && selectedInvoice && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Charge Card
                </h3>
                <button
                  onClick={() => {
                    setShowChargeCardModal(false);
                    setSelectedPaymentMethodId('');
                    setPhysicianPaymentMethods([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-600">Invoice</div>
                <div className="font-medium">{selectedInvoice.invoiceNumber}</div>
                <div className="text-lg font-bold text-blue-600 mt-1">
                  {formatCurrency(selectedInvoice.amountDue)} due
                </div>
              </div>

              {loadingPaymentMethods ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                  <span className="text-gray-600">Loading payment methods...</span>
                </div>
              ) : physicianPaymentMethods.length === 0 ? (
                <div className="text-center py-6">
                  <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 mb-2">No saved payment methods</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Add a new card to pay this invoice. The card will be saved for future payments.
                  </p>
                  <button
                    onClick={() => {
                      setShowChargeCardModal(false);
                      setShowAddCardModal(true);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add New Card & Pay
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Payment Method
                    </label>
                    <div className="space-y-2">
                      {physicianPaymentMethods.map((pm) => (
                        <label
                          key={pm.payment_method_id}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                            selectedPaymentMethodId === pm.payment_method_id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={pm.payment_method_id}
                            checked={selectedPaymentMethodId === pm.payment_method_id}
                            onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center">
                              <CreditCard className="h-4 w-4 text-gray-400 mr-2" />
                              <span className="font-medium">
                                {pm.card?.card_brand || 'Card'} •••• {pm.card?.last4 || '****'}
                              </span>
                              {pm.is_default && (
                                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                  Default
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              Expires {pm.card?.exp_month}/{pm.card?.exp_year}
                            </div>
                          </div>
                        </label>
                      ))}

                      {/* Add New Card Option */}
                      <button
                        onClick={() => {
                          setShowChargeCardModal(false);
                          setShowAddCardModal(true);
                        }}
                        className="flex items-center p-3 border border-dashed border-gray-300 rounded-lg cursor-pointer transition-colors hover:border-blue-400 hover:bg-blue-50 w-full text-left"
                      >
                        <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-full mr-3">
                          <Plus className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <span className="font-medium text-blue-600">Add New Card</span>
                          <p className="text-sm text-gray-500">Save a new card and pay</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={chargeCard}
                      disabled={chargingCard || !selectedPaymentMethodId}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {chargingCard ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Charging...
                        </>
                      ) : (
                        <>
                          <CreditCard className="h-4 w-4 mr-2" />
                          Charge {formatCurrency(selectedInvoice.amountDue)}
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setShowChargeCardModal(false);
                        setSelectedPaymentMethodId('');
                        setPhysicianPaymentMethods([]);
                      }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invoice Details Modal - Responsive */}
      {showDetailsModal && selectedInvoice && (
        <>
          {/* Mobile: Full screen */}
          <div className="md:hidden fixed inset-0 bg-white z-[60]">
            <div className="flex flex-col h-full">
              {/* Mobile Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="h-5 w-5 mr-2" />
                  Back
                </button>
                <h1 className="text-lg font-medium text-gray-900 truncate">
                  {selectedInvoice.invoiceNumber}
                </h1>
                <div className="w-16" /> {/* Spacer for centering */}
              </div>
              
              {/* Mobile Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-6">
                  {/* Status Badge */}
                  <div className="flex justify-center">
                    {getStatusBadge(selectedInvoice)}
                  </div>
                  
                  {/* Invoice Basic Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Invoice Information</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Created:</span>
                        <span>{formatDate(selectedInvoice.createdAt)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Due Date:</span>
                        <span>{formatDate(selectedInvoice.dueDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Payment Terms:</span>
                        <span className="capitalize">
                          {selectedInvoice.physicianId?.physicianData?.arEnabled
                            ? selectedInvoice.paymentTerms
                            : 'Due upon receipt'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">AR Status:</span>
                        {selectedInvoice.physicianId?.physicianData?.arEnabled ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                            <CreditCard className="h-3 w-3 mr-1" />
                            AR Enabled
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                            Prepay Required
                          </span>
                        )}
                      </div>
                      {selectedInvoice.paymentLinkSentAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Payment Link Sent:</span>
                          <span className="text-purple-600">
                            {formatDate(selectedInvoice.paymentLinkSentAt)}
                          </span>
                        </div>
                      )}
                      <InvoiceOrderLinks invoice={selectedInvoice} />
                    </div>
                  </div>

                  {/* Physician Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Physician</h3>
                    <div className="space-y-1 text-sm">
                      <div className="font-medium">Dr. {selectedInvoice.physicianId?.profile?.firstName} {selectedInvoice.physicianId?.profile?.lastName}</div>
                      <div className="text-gray-500">{selectedInvoice.physicianId?.email}</div>
                    </div>
                  </div>

                  {/* Clinic Address */}
                  {selectedInvoice.physicianId?.physicianData?.clinicAddress && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-3">Clinic Address</h3>
                      <div className="space-y-1 text-sm">
                        {selectedInvoice.physicianId?.profile?.clinicName && (
                          <div className="font-medium">{selectedInvoice.physicianId.profile.clinicName}</div>
                        )}
                        <div className="text-gray-600">
                          {selectedInvoice.physicianId.physicianData.clinicAddress.street}
                        </div>
                        {selectedInvoice.physicianId.physicianData.clinicAddress.street2 && (
                          <div className="text-gray-600">
                            {selectedInvoice.physicianId.physicianData.clinicAddress.street2}
                          </div>
                        )}
                        <div className="text-gray-600">
                          {selectedInvoice.physicianId.physicianData.clinicAddress.city}, {selectedInvoice.physicianId.physicianData.clinicAddress.state} {selectedInvoice.physicianId.physicianData.clinicAddress.zipCode}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Account Manager */}
                  {selectedInvoice.physicianId?.physicianData?.distributorId && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-3">Account Manager</h3>
                      <div className="space-y-1 text-sm">
                        <div className="font-medium">{getAccountManagerName(selectedInvoice)}</div>
                        <div className="text-gray-500">{getAccountManagerEmail(selectedInvoice)}</div>
                      </div>
                    </div>
                  )}
                  
                  {/* Order Created By */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Order Placed By</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        {selectedInvoice.createdByRole === 'admin' ? (
                          <User className="h-4 w-4 text-purple-500 mr-2" />
                        ) : (
                          <Building2 className="h-4 w-4 text-green-500 mr-2" />
                        )}
                        <span className="capitalize font-medium">
                          {selectedInvoice.createdByRole === 'admin' ? 'Admin' : 'Account Manager'}
                        </span>
                      </div>
                      {selectedInvoice.createdById && (
                        <>
                          <div>
                            {selectedInvoice.createdById.profile?.firstName} {selectedInvoice.createdById.profile?.lastName}
                          </div>
                          <div className="text-gray-500">{selectedInvoice.createdById.email}</div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Financial Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-medium text-gray-900 mb-3">Financial Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Subtotal:</span>
                        <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                      </div>
                      {selectedInvoice.shippingAmount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Shipping:</span>
                          <span>{formatCurrency(selectedInvoice.shippingAmount)}</span>
                        </div>
                      )}
                      {(selectedInvoice.telemedNetworkFee || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Telemed network fee:</span>
                          <span>{formatCurrency(selectedInvoice.telemedNetworkFee || 0)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">Tax:</span>
                        <span>{formatCurrency(selectedInvoice.taxAmount)}</span>
                      </div>
                      {selectedInvoice.volumeReward?.discountAmount ? (
                        selectedInvoice.volumeReward.appliedAs === 'physician_markup' ? (
                          // Patient-pay: reward does NOT reduce the total — it's booked
                          // as physician markup. Shown as an informational credit line.
                          <div className="flex justify-between text-indigo-600">
                            <span>
                              Volume Reward ({selectedInvoice.volumeReward.tierLabel} {selectedInvoice.volumeReward.discountPercent}%) — physician markup credit:
                            </span>
                            <span>+{formatCurrency(selectedInvoice.volumeReward.discountAmount)}</span>
                          </div>
                        ) : (
                          // Clinic-pay: reward is a discount off the total.
                          <div className="flex justify-between text-indigo-600">
                            <span>
                              Volume Reward ({selectedInvoice.volumeReward.tierLabel} {selectedInvoice.volumeReward.discountPercent}%):
                            </span>
                            <span>−{formatCurrency(selectedInvoice.volumeReward.discountAmount)}</span>
                          </div>
                        )
                      ) : null}
                      {/* Deal lines are exempt from the reward discount, so the
                          discount is less than N% of the invoice. Say why. */}
                      {selectedInvoice.volumeReward?.excludedDealSubtotal ? (
                        <div className="text-xs text-gray-500 italic">
                          {formatCurrency(selectedInvoice.volumeReward.excludedDealSubtotal)} of
                          negotiated-deal pricing is excluded from the volume reward.
                        </div>
                      ) : null}
                      <div className="flex justify-between font-medium text-base border-t border-gray-300 pt-2">
                        <span>Total:</span>
                        <span>{formatCurrency(selectedInvoice.totalAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Amount Paid:</span>
                        <span className="text-green-600">{formatCurrency(selectedInvoice.amountPaid)}</span>
                      </div>
                      <div className="flex justify-between font-medium border-t border-gray-300 pt-2">
                        <span>Amount Due:</span>
                        <span className={selectedInvoice.amountDue > 0 ? 'text-red-600' : 'text-green-600'}>
                          {formatCurrency(selectedInvoice.amountDue)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Invoice Items - Mobile Cards */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">Invoice Items</h3>
                    <div className="space-y-3">
                      {selectedInvoice.items.map((item, index) => (
                        <div key={index} className={`rounded-lg p-4 ${(item as any).isSupply ? 'bg-amber-50' : 'bg-gray-50'}`}>
                          <div className="font-medium text-sm mb-2">
                            {item.description}
                            {(item as any).isSupply && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Supply</span>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2 text-xs text-gray-600">
                            <div>
                              <span className="block text-gray-500">Qty:</span>
                              <span className="font-medium">{item.quantity}</span>
                            </div>
                            <div>
                              <span className="block text-gray-500">Unit Price:</span>
                              <span className="font-medium">{formatCurrency(item.unitPrice)}</span>
                            </div>
                            <div>
                              <span className="block text-gray-500">Total:</span>
                              <span className="font-medium">{formatCurrency(item.totalPrice)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Payment History - Mobile */}
                  {selectedInvoice.paymentHistory && selectedInvoice.paymentHistory.length > 0 && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-3">Payment History</h3>
                      <div className="space-y-3">
                        {selectedInvoice.paymentHistory.map((payment, index) => {
                          const isFailed = payment.status === 'failed';
                          const isRefunded = payment.status === 'refunded';
                          return (
                            <div key={index} className={`rounded-lg p-4 ${isFailed ? 'bg-red-50' : isRefunded ? 'bg-amber-50' : 'bg-gray-50'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center">
                                  <DollarSign className={`h-4 w-4 mr-2 ${isFailed ? 'text-red-500' : isRefunded ? 'text-amber-500' : 'text-green-500'}`} />
                                  <span className="font-medium">{isRefunded ? '-' : ''}{formatCurrency(payment.amount)}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                                    isFailed
                                      ? 'bg-red-100 text-red-800'
                                      : isRefunded
                                        ? 'bg-amber-100 text-amber-800'
                                        : 'bg-green-100 text-green-800'
                                  }`}>
                                    {payment.paymentMethod}
                                  </span>
                                  {isFailed && (
                                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                                      Declined
                                    </span>
                                  )}
                                  {isRefunded && (
                                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                                      Refunded
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-sm text-gray-600">
                                <div>{formatDate(payment.paymentDate)}</div>
                                {payment.transactionId && (
                                  <div className="font-mono text-xs mt-1">
                                    ID: {payment.transactionId}
                                  </div>
                                )}
                                {isFailed && payment.errorMessage && (
                                  <div className="text-red-600 mt-1">
                                    {payment.errorMessage}
                                  </div>
                                )}
                                {!isFailed && payment.notes && (
                                  <div className="italic mt-1">"{payment.notes}"</div>
                                )}
                                <div className="text-xs text-gray-500 mt-1">
                                  {isFailed ? null : payment.notes?.toLowerCase().includes('automatic') || payment.notes?.toLowerCase().includes('hyperswitch') ? (
                                    <span className="text-purple-600">Automatic (Hyperswitch)</span>
                                  ) : payment.recordedById?.profile?.firstName || payment.recordedById?.profile?.lastName ? (
                                    `Recorded by: ${payment.recordedById.profile?.firstName || ''} ${payment.recordedById.profile?.lastName || ''}`.trim()
                                  ) : (
                                    'Recorded by: Admin'
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Payment Status - Mobile */}
                  {selectedInvoice.status === 'paid' && selectedInvoice.paidAt && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                          <div>
                            <div className="font-medium text-green-800">Invoice Fully Paid</div>
                            <div className="text-sm text-green-600">
                              Marked as paid on {formatDate(selectedInvoice.paidAt)}
                            </div>
                          </div>
                        </div>
                        {canIssueRefunds && (
                          <button
                            onClick={() => setShowRefundModal(true)}
                            className="px-3 py-1.5 text-sm font-medium text-orange-700 bg-white border border-orange-300 rounded-md hover:bg-orange-50"
                          >
                            Refund
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons - Mobile */}
                  {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'voided' && selectedInvoice.status !== 'refunded' && (
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="font-medium text-gray-900 mb-3">Actions</h3>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setShowDetailsModal(false);
                            openChargeCardModal(selectedInvoice);
                          }}
                          className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Charge
                        </button>
                        {canRecordPayment && (
                        <button
                          onClick={() => {
                            setShowDetailsModal(false);
                            setShowPaymentModal(true);
                          }}
                          className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                        >
                          <DollarSign className="h-4 w-4 mr-1" />
                          Record
                        </button>
                        )}
                        <button
                          onClick={() => sendPaymentLink(selectedInvoice._id)}
                          disabled={sendingPaymentLink === selectedInvoice._id}
                          className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                        >
                          {sendingPaymentLink === selectedInvoice._id ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4 mr-1" />
                          )}
                          {selectedInvoice.paymentLinkSentAt ? 'Resend' : 'Send Link'}
                        </button>
                        {selectedInvoice.amountPaid === 0 && (
                          <button
                            onClick={() => setInvoiceToVoid(selectedInvoice)}
                            disabled={cancellingInvoice === selectedInvoice._id}
                            className="inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            {cancellingInvoice === selectedInvoice._id ? '...' : 'Void'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop: Modal */}
          <div
            className="hidden md:block fixed inset-0 -top-6 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-[60]"
            onClick={() => setShowDetailsModal(false)}
          >
            <div
              className="relative top-10 mx-auto p-5 border w-2/3 max-w-4xl shadow-lg rounded-md bg-white"
              onClick={(e) => e.stopPropagation()}
            >
            <div className="mt-3">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Invoice Details - {selectedInvoice.invoiceNumber}
                </h3>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => printInvoice(selectedInvoice)}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Print
                  </button>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
              
              {/* First row: Invoice Info + Financial Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Invoice Info */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Invoice Information</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Status:</span>
                      <span>{getStatusBadge(selectedInvoice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Created:</span>
                      <span>{formatDate(selectedInvoice.createdAt)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Due Date:</span>
                      <span>{formatDate(selectedInvoice.dueDate)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Payment Terms:</span>
                      <span className="capitalize">
                        {selectedInvoice.physicianId?.physicianData?.arEnabled
                          ? selectedInvoice.paymentTerms
                          : 'Due upon receipt'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">AR Status:</span>
                      {selectedInvoice.physicianId?.physicianData?.arEnabled ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          <CreditCard className="h-3 w-3 mr-1" />
                          AR Enabled
                          {selectedInvoice.physicianId.physicianData.creditLimit !== null &&
                           selectedInvoice.physicianId.physicianData.creditLimit !== undefined && (
                            <span className="ml-1 text-blue-600">
                              (${selectedInvoice.physicianId.physicianData.creditLimit.toLocaleString()} limit)
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          Prepay Required
                        </span>
                      )}
                    </div>
                    {selectedInvoice.paymentLinkSentAt && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Payment Link Sent:</span>
                        <span className="text-purple-600">
                          {formatDate(selectedInvoice.paymentLinkSentAt)}
                        </span>
                      </div>
                    )}
                    <InvoiceOrderLinks invoice={selectedInvoice} />
                  </div>
                </div>

                {/* Financial Summary */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Financial Summary</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Subtotal:</span>
                      <span>{formatCurrency(selectedInvoice.subtotal)}</span>
                    </div>
                    {selectedInvoice.shippingAmount > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Shipping:</span>
                        <span>{formatCurrency(selectedInvoice.shippingAmount)}</span>
                      </div>
                    )}
                    {(selectedInvoice.telemedNetworkFee || 0) > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Telemed network fee:</span>
                        <span>{formatCurrency(selectedInvoice.telemedNetworkFee || 0)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Tax:</span>
                      <span>{formatCurrency(selectedInvoice.taxAmount)}</span>
                    </div>
                    {selectedInvoice.volumeReward?.discountAmount ? (
                      selectedInvoice.volumeReward.appliedAs === 'physician_markup' ? (
                        <div className="flex justify-between text-indigo-600">
                          <span>Volume Reward ({selectedInvoice.volumeReward.tierLabel} {selectedInvoice.volumeReward.discountPercent}%) — physician markup credit:</span>
                          <span>+{formatCurrency(selectedInvoice.volumeReward.discountAmount)}</span>
                        </div>
                      ) : (
                        <div className="flex justify-between text-indigo-600">
                          <span>Volume Reward ({selectedInvoice.volumeReward.tierLabel} {selectedInvoice.volumeReward.discountPercent}%):</span>
                          <span>−{formatCurrency(selectedInvoice.volumeReward.discountAmount)}</span>
                        </div>
                      )
                    ) : null}
                    <div className="flex justify-between font-medium">
                      <span>Total:</span>
                      <span>{formatCurrency(selectedInvoice.totalAmount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Amount Paid:</span>
                      <span className="text-green-600">{formatCurrency(selectedInvoice.amountPaid)}</span>
                    </div>
                    <div className="flex justify-between font-medium">
                      <span>Amount Due:</span>
                      <span className={selectedInvoice.amountDue > 0 ? 'text-red-600' : 'text-green-600'}>
                        {formatCurrency(selectedInvoice.amountDue)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <hr className="my-6 border-gray-200" />

              {/* Second row: Physician/Clinic + Account Manager/Order Placed By */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left: Physician & Clinic */}
                <div className="space-y-4">
                  {/* Physician Info */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Physician</h4>
                    <div className="space-y-1 text-sm">
                      <div>Dr. {selectedInvoice.physicianId?.profile?.firstName} {selectedInvoice.physicianId?.profile?.lastName}</div>
                      <div className="text-gray-500">{selectedInvoice.physicianId?.email}</div>
                    </div>
                  </div>

                  {/* Clinic Address */}
                  {selectedInvoice.physicianId?.physicianData?.clinicAddress && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Clinic Address</h4>
                      <div className="space-y-1 text-sm">
                        {selectedInvoice.physicianId?.profile?.clinicName && (
                          <div className="font-medium">{selectedInvoice.physicianId.profile.clinicName}</div>
                        )}
                        <div className="text-gray-600">
                          {selectedInvoice.physicianId.physicianData.clinicAddress.street}
                        </div>
                        {selectedInvoice.physicianId.physicianData.clinicAddress.street2 && (
                          <div className="text-gray-600">
                            {selectedInvoice.physicianId.physicianData.clinicAddress.street2}
                          </div>
                        )}
                        <div className="text-gray-600">
                          {selectedInvoice.physicianId.physicianData.clinicAddress.city}, {selectedInvoice.physicianId.physicianData.clinicAddress.state} {selectedInvoice.physicianId.physicianData.clinicAddress.zipCode}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Right: Account Manager & Order Placed By */}
                <div className="space-y-4">
                  {/* Account Manager */}
                  {selectedInvoice.physicianId?.physicianData?.distributorId && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Account Manager</h4>
                      <div className="space-y-1 text-sm">
                        <div className="font-medium">{getAccountManagerName(selectedInvoice)}</div>
                        <div className="text-gray-500">{getAccountManagerEmail(selectedInvoice)}</div>
                      </div>
                    </div>
                  )}

                  {/* Order Created By */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Order Placed By</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center">
                        {selectedInvoice.createdByRole === 'admin' ? (
                          <User className="h-4 w-4 text-purple-500 mr-2" />
                        ) : (
                          <Building2 className="h-4 w-4 text-green-500 mr-2" />
                        )}
                        <span className="capitalize font-medium">
                          {selectedInvoice.createdByRole === 'admin' ? 'Admin' : 'Account Manager'}
                        </span>
                      </div>
                      {selectedInvoice.createdById && (
                        <>
                          <div>
                            {selectedInvoice.createdById.profile?.firstName} {selectedInvoice.createdById.profile?.lastName}
                          </div>
                          <div className="text-gray-500">{selectedInvoice.createdById.email}</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <hr className="my-6 border-gray-200" />

              {/* Invoice Items */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Invoice Items</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedInvoice.items.map((item, index) => (
                        <tr key={index} className={(item as any).isSupply ? 'bg-amber-50/50' : ''}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            {item.description}
                            {(item as any).isSupply && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Supply</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-900">{item.quantity}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.unitPrice)}</td>
                          <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.totalPrice)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Payment History */}
              {selectedInvoice.paymentHistory && selectedInvoice.paymentHistory.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-2">Payment History</h4>
                  <div className="space-y-3">
                    {selectedInvoice.paymentHistory.map((payment, index) => {
                      const isFailed = payment.status === 'failed';
                      const isRefunded = payment.status === 'refunded';
                      return (
                        <div key={index} className={`border rounded-lg p-3 ${
                          isFailed ? 'border-red-200 bg-red-50' : isRefunded ? 'border-amber-200 bg-amber-50' : 'border-gray-200'
                        }`}>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <DollarSign className={`h-4 w-4 ${isFailed ? 'text-red-500' : isRefunded ? 'text-amber-500' : 'text-green-500'}`} />
                                <span className="font-medium text-lg">{isRefunded ? '-' : ''}{formatCurrency(payment.amount)}</span>
                                <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                                  isFailed
                                    ? 'bg-red-100 text-red-800'
                                    : isRefunded
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-green-100 text-green-800'
                                }`}>
                                  {payment.paymentMethod}
                                </span>
                                {isFailed && (
                                  <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                                    Declined
                                  </span>
                                )}
                                {isRefunded && (
                                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                                    Refunded
                                  </span>
                                )}
                              </div>
                              {payment.transactionId && (
                                <div className="text-sm text-gray-600 mb-1">
                                  Transaction ID: <span className="font-mono">{payment.transactionId}</span>
                                </div>
                              )}
                              {isFailed && payment.errorMessage && (
                                <div className="text-sm text-red-600 mb-1">
                                  {payment.errorMessage}
                                </div>
                              )}
                              {!isFailed && payment.notes && (
                                <div className="text-sm text-gray-600 italic">
                                  "{payment.notes}"
                                </div>
                              )}
                            </div>
                            <div className="text-right text-sm">
                              <div className="font-medium">{formatDate(payment.paymentDate)}</div>
                              <div className="text-gray-500 text-xs mt-1">
                                {isFailed ? (
                                  <span className="text-red-600">Payment Failed</span>
                                ) : payment.notes?.toLowerCase().includes('automatic') || payment.notes?.toLowerCase().includes('hyperswitch') ? (
                                  <span className="text-purple-600">Automatic (Hyperswitch)</span>
                                ) : payment.recordedById?.profile?.firstName || payment.recordedById?.profile?.lastName ? (
                                  `Recorded by: ${payment.recordedById.profile?.firstName || ''} ${payment.recordedById.profile?.lastName || ''}`.trim()
                                ) : (
                                  'Recorded by: Admin'
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Payment Status */}
              {selectedInvoice.status === 'paid' && selectedInvoice.paidAt && (
                <div className="mt-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                        <div>
                          <div className="font-medium text-green-800">Invoice Fully Paid</div>
                          <div className="text-sm text-green-600">
                            Marked as paid on {formatDate(selectedInvoice.paidAt)}
                          </div>
                        </div>
                      </div>
                      {canIssueRefunds && (
                        <button
                          onClick={() => setShowRefundModal(true)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-orange-700 bg-white border border-orange-300 rounded-md hover:bg-orange-50"
                        >
                          Refund
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'voided' && selectedInvoice.status !== 'refunded' && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">Actions</h4>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        openChargeCardModal(selectedInvoice);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Charge Card
                    </button>
                    {canRecordPayment && (
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        setShowPaymentModal(true);
                      }}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Record Payment
                    </button>
                    )}
                    <button
                      onClick={() => sendPaymentLink(selectedInvoice._id)}
                      disabled={sendingPaymentLink === selectedInvoice._id}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                      title={selectedInvoice.paymentLinkSentAt ? `Last sent ${formatShortDate(selectedInvoice.paymentLinkSentAt)}` : 'Send payment link via email'}
                    >
                      {sendingPaymentLink === selectedInvoice._id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Mail className="h-4 w-4 mr-2" />
                      )}
                      {selectedInvoice.paymentLinkSentAt ? 'Resend Payment Link' : 'Send Payment Link'}
                    </button>
                    {selectedInvoice.amountPaid === 0 && (
                      <button
                        onClick={() => setInvoiceToVoid(selectedInvoice)}
                        disabled={cancellingInvoice === selectedInvoice._id}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        {cancellingInvoice === selectedInvoice._id ? 'Voiding...' : 'Void Invoice'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </>
      )}

      {/* Add Card & Pay Invoice Modal */}
      {selectedInvoice && (
        <InvoicePaymentModal
          isOpen={showAddCardModal}
          onClose={() => {
            setShowAddCardModal(false);
            setSelectedInvoice(null);
          }}
          onSuccess={() => {
            setShowAddCardModal(false);
            setSelectedInvoice(null);
            loadInvoices();
            toast.success('Payment successful! Invoice has been paid.');
          }}
          invoice={{
            _id: selectedInvoice._id,
            invoiceNumber: selectedInvoice.invoiceNumber,
            amountDue: selectedInvoice.amountDue,
            physicianName: `Dr. ${selectedInvoice.physicianId?.profile?.firstName || ''} ${selectedInvoice.physicianId?.profile?.lastName || ''}`.trim(),
            clinicName: selectedInvoice.physicianId?.profile?.clinicName
          }}
          saveCard={true}
          title="Add Card & Pay Invoice"
          allowPartialPayment={true}
        />
      )}

      {/* Credit Memo Modal */}
      {showCreditModal && selectedInvoice && (
        <IssueCreditModal
          invoice={selectedInvoice}
          onClose={() => { setShowCreditModal(false); setSelectedInvoice(null); }}
          onSuccess={() => loadInvoices()}
        />
      )}

      {/* Edit Draft Modal */}
      {showEditDraftModal && selectedInvoice && (
        <EditDraftModal
          invoice={selectedInvoice}
          onClose={() => { setShowEditDraftModal(false); setSelectedInvoice(null); }}
          onSuccess={() => loadInvoices()}
        />
      )}

      {/* Refund Invoice Modal */}
      {showRefundModal && selectedInvoice && (
        <RefundInvoiceModal
          invoice={selectedInvoice as any}
          onClose={() => setShowRefundModal(false)}
          onSuccess={() => { setShowRefundModal(false); setShowDetailsModal(false); loadInvoices(); }}
        />
      )}

      {/* Void Invoice confirmation */}
      {invoiceToVoid && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Void invoice?</h2>
            </div>

            <div className="px-6 py-4">
              <p className="text-gray-700 mb-4">
                This will void invoice{' '}
                <strong className="font-mono">{invoiceToVoid.invoiceNumber}</strong>. This action cannot be undone.
              </p>

              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Physician:</span>
                  <span className="text-sm text-right">
                    {invoiceToVoid.physicianId?.profile?.clinicName
                      || `${invoiceToVoid.physicianId?.profile?.firstName || ''} ${invoiceToVoid.physicianId?.profile?.lastName || ''}`.trim()
                      || '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount due:</span>
                  <span className="font-semibold">{formatCurrency(invoiceToVoid.amountDue)}</span>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setInvoiceToVoid(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => cancelInvoice(invoiceToVoid._id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Void invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvoices;
